import { useState, useEffect, useContext } from "react";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

// Admin UIDs for elevated access
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

const ENABLE_LOGGING = false;

const log = (...args) => {
  if (ENABLE_LOGGING) {
    console.log("[useTeamAccess]", ...args);
  }
};

const useTeamAccess = () => {
  const { user, isAuthChecking } = useContext(AuthContext);

  // State
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [accessState, setAccessState] = useState("loading"); // "loading", "no-auth", "no-teams", "team-selector", "ready"

  // Computed values
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // Fetch teams where user is owner/creator/reader
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user) {
        setTeamsLoading(false);
        setAccessState("no-auth");
        return;
      }

      try {
        setTeamsLoading(true);
        setAccessState("loading");
        log("Fetching user teams...");

        const teamsRef = collection(db, "teams");
        const ownerQ = query(
          teamsRef,
          where("owners", "array-contains", user.uid)
        );
        const creatorQ = query(
          teamsRef,
          where("creators", "array-contains", user.email)
        );
        const readerQ = query(
          teamsRef,
          where("readers", "array-contains", user.email)
        );

        const [ownSnap, creSnap, readSnap] = await Promise.all([
          getDocs(ownerQ),
          getDocs(creatorQ),
          getDocs(readerQ),
        ]);

        const map = new Map();
        ownSnap.forEach((d) =>
          map.set(d.id, { id: d.id, ...d.data(), role: "owner" })
        );
        creSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "creator" });
        });
        readSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "reader" });
        });

        const userTeams = Array.from(map.values());
        setTeams(userTeams);
        log("Found teams:", userTeams);

        // Determine access state
        if (userTeams.length === 0) {
          setAccessState("no-teams");
        } else if (userTeams.length === 1) {
          // Auto-select single team
          setSelectedTeamId(userTeams[0].id);
          setSelectedTeam(userTeams[0]);
          setAccessState("ready");
          log("Auto-selected single team:", userTeams[0]);
        } else {
          // Multiple teams - need selection
          setAccessState("team-selector");
        }
      } catch (err) {
        console.error("Error fetching teams:", err);
        toast.error("Failed to load teams");
        setAccessState("no-teams");
      } finally {
        setTeamsLoading(false);
      }
    };

    if (!isAuthChecking && user) {
      fetchUserTeams();
    } else if (!isAuthChecking && !user) {
      setTeamsLoading(false);
      setAccessState("no-auth");
    }
  }, [user, isAuthChecking]);

  // Team selection handler
  const selectTeam = (team) => {
    setSelectedTeamId(team.id);
    setSelectedTeam(team);
    setAccessState("ready");
    log("Team selected:", team);
  };

  // Clear team selection
  const clearTeamSelection = () => {
    setSelectedTeamId(null);
    setSelectedTeam(null);
    setAccessState(teams.length > 1 ? "team-selector" : "no-teams");
  };

  // Check team permissions
  const checkTeamPermissions = async (teamId) => {
    if (!user || !teamId) return null;

    try {
      const teamRef = doc(db, "teams", teamId);
      const teamDoc = await getDoc(teamRef);

      if (!teamDoc.exists()) {
        return { exists: false };
      }

      const teamData = teamDoc.data();
      const userHasOwnerAccess =
        user && teamData.owners && teamData.owners.includes(user.uid);
      const userHasCreatorAccess =
        user && teamData.creators && teamData.creators.includes(user.email);
      const userHasReaderAccess =
        user && teamData.readers && teamData.readers.includes(user.email);

      return {
        exists: true,
        teamData,
        access: {
          isOwner: userHasOwnerAccess,
          isCreator: userHasCreatorAccess,
          isReader: userHasReaderAccess,
          hasAnyAccess:
            userHasOwnerAccess || userHasCreatorAccess || userHasReaderAccess,
        },
      };
    } catch (err) {
      console.error("Error checking team permissions:", err);
      return { exists: false, error: err.message };
    }
  };

  return {
    // State
    teams,
    selectedTeamId,
    selectedTeam,
    teamsLoading,
    accessState,

    // Computed
    isAdmin,
    isReady: accessState === "ready",
    needsTeamSelection: accessState === "team-selector",
    hasNoTeams: accessState === "no-teams",
    needsAuth: accessState === "no-auth",
    isLoading: accessState === "loading" || isAuthChecking,

    // Actions
    selectTeam,
    clearTeamSelection,
    checkTeamPermissions,
  };
};

export default useTeamAccess;
